import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, List, ListItemButton, ListItemText, TextField,
  Switch, FormControlLabel, MenuItem, Grid, Chip, Alert, Snackbar, alpha, useTheme,
  InputAdornment, Divider, Drawer, useMediaQuery, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Link, CircularProgress,
} from '@mui/material';
import {
  Tune, Palette, DirectionsCar, Restaurant, ShoppingCart, Payment, Notifications,
  Security, Stars, Subscriptions, Hub, Psychology, Save, Lock, Translate, Handshake, Menu, Storage,
  SystemUpdate, Undo,
} from '@mui/icons-material';
import SettingFileUpload from '../../components/settings/SettingFileUpload';
import LocaleCurrencyPanel from './LocaleCurrencyPanel';
import { refreshTenantSession } from '../../hooks/useTenantBootstrap';
import {
  getSettingsCatalog, getSettings, getSettingsContext, bulkUpsertSettings, runPushTest, generateVapidKeys, getSettingsHistory,
  testStorageConnection, type StorageTestResult, isGateOpen, testIntegrationSetting,
  type IntegrationTestKey, type IntegrationTestResult, testAiConnection, type AiTestResult,
  type PushTestResult, type SettingCategoryDef, type SettingRow, type SettingKeyDef, type SettingsContext,
} from '../../services/platform/settingsService';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  general: <Tune fontSize="small" />,
  branding: <Palette fontSize="small" />,
  transport: <DirectionsCar fontSize="small" />,
  food: <Restaurant fontSize="small" />,
  grocery: <ShoppingCart fontSize="small" />,
  payment: <Payment fontSize="small" />,
  notification: <Notifications fontSize="small" />,
  security: <Security fontSize="small" />,
  loyalty: <Stars fontSize="small" />,
  subscription: <Subscriptions fontSize="small" />,
  integration: <Hub fontSize="small" />,
  ai: <Psychology fontSize="small" />,
  partner_onboarding: <Handshake fontSize="small" />,
  locale: <Translate fontSize="small" />,
  storage: <Storage fontSize="small" />,
  mobile_versioning: <SystemUpdate fontSize="small" />,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function tenantCurrency(ctx: SettingsContext | undefined) {
  return ctx?.currencies.find((c) => c.value === ctx.tenant.currency);
}

const SEARCH_TIMEOUT_SEC_PER_KM = 4;
const SEARCH_TIMEOUT_MIN_SEC = 60;

/** Rules across keys — same as the backend's validateCrossFieldRules. */
function crossFieldErrors(values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {};
  const radius = Number(values['transport.max_search_radius_km']);
  const timeout = Number(values['transport.driver_search_timeout_sec']);
  if (Number.isFinite(radius) && Number.isFinite(timeout) && radius > 0 && timeout > 0) {
    const needed = Math.max(SEARCH_TIMEOUT_MIN_SEC, Math.ceil(radius * SEARCH_TIMEOUT_SEC_PER_KM));
    if (timeout < needed) {
      const msg = `Search Timeout ${timeout} s is too short for a ${radius} km radius — use at least ${needed} s or reduce the radius`;
      errors['transport.driver_search_timeout_sec'] = msg;
      errors['transport.max_search_radius_km'] = msg;
    }
  }
  if (values['storage.s3_enabled'] === 'true') {
    const missing = S3_REQUIRED_KEYS.filter(([k]) => !String(values[`storage.${k}`] ?? '').trim());
    for (const [k, label] of missing) errors[`storage.${k}`] = `${label} is required while S3 is enabled`;
    if (missing.length) errors['storage.s3_enabled'] = `Enter ${missing.map(([, l]) => l).join(', ')} before enabling S3`;
  }
  const ai = (k: string) => String(values[`ai.${k}`] ?? '').trim();
  if (ai('ai_enabled') === 'true') {
    const missing: Array<[string, string]> = [];
    if (!ai('ai_api_key')) missing.push(['ai_api_key', 'AI API Key']);
    if (!ai('ai_model')) missing.push(['ai_model', 'Default Model']);
    if (ai('ai_provider') === 'openai_compatible' && !ai('ai_endpoint')) missing.push(['ai_endpoint', 'API Endpoint']);
    for (const [k, label] of missing) errors[`ai.${k}`] = `${label} is required while AI is enabled`;
    if (missing.length) errors['ai.ai_enabled'] = `Enter ${missing.map(([, l]) => l).join(', ')} before enabling AI`;
    const key = ai('ai_api_key');
    if (key && !/[*•]{3,}/.test(key) && ai('ai_provider') !== 'openai_compatible' && !key.startsWith('sk-')) {
      errors['ai.ai_api_key'] = 'OpenAI API keys start with "sk-" — for another provider choose "OpenAI-compatible API"';
    }
  }
  if (ai('ocr_enabled') === 'true' && ai('ai_enabled') !== 'true') {
    errors['ai.ocr_enabled'] = 'OCR reads documents with the AI model — turn on AI Enabled and configure the provider first';
  }
  const provider = values['integration.analytics_provider'] || 'none';
  if (provider !== 'none') {
    const id = String(values['integration.analytics_id'] ?? '').trim();
    const ga = provider === 'google_analytics';
    if (!id) {
      errors['integration.analytics_id'] = `Enter the ${ga ? 'GA4 Measurement ID' : 'Mixpanel project token'} — or set the provider to None`;
      errors['integration.analytics_provider'] = `${ga ? 'Google Analytics 4' : 'Mixpanel'} is selected but the Analytics ID is empty`;
    } else if (ga && !GA4_ID_RE.test(id.toUpperCase())) {
      errors['integration.analytics_id'] = 'Google Analytics 4 needs a Measurement ID like G-XXXXXXXXXX';
    } else if (!ga && !MIXPANEL_TOKEN_RE.test(id.toLowerCase())) {
      errors['integration.analytics_id'] = 'Mixpanel needs the 32-character project token';
    }
  }
  return errors;
}

const INTEGRATION_TEST_KEYS: IntegrationTestKey[] = ['maps_api_key', 'geo_coder_api_key', 'socket_realtime_url'];

function integrationVerification(ctx: SettingsContext | undefined, key: IntegrationTestKey) {
  const i = ctx?.integration;
  if (!i) return null;
  return key === 'maps_api_key' ? i.maps.verification : key === 'geo_coder_api_key' ? i.geocoder.verification : i.socket.verification;
}

const GA4_ID_RE = /^G-[A-Z0-9]{4,12}$/;
const MIXPANEL_TOKEN_RE = /^[a-f0-9]{32}$/;

const S3_REQUIRED_KEYS: Array<[string, string]> = [
  ['s3_bucket_name', 'S3 Bucket Name'],
  ['s3_region', 'S3 Region'],
  ['s3_access_key_id', 'S3 Access Key ID'],
  ['s3_secret_access_key', 'S3 Secret Access Key'],
];

const PLACEHOLDER_DOMAIN_RE = /(^|\.)(demo\.com|example\.(com|org|net)|test|invalid|localhost|local|localdomain)$/i;

function formatError(format: SettingKeyDef['format'], value: string): string | null {
  switch (format) {
    case 'sender_email': {
      if (!EMAIL_RE.test(value)) return 'Enter a valid sender address, e.g. noreply@yourcompany.com';
      const domain = value.split('@')[1];
      return PLACEHOLDER_DOMAIN_RE.test(domain) ? `${domain} is a demo/placeholder domain — use a domain you send mail from` : null;
    }
    case 'push_subject': {
      if (value.startsWith('mailto:')) {
        const email = value.slice(7);
        if (!EMAIL_RE.test(email)) return 'Use mailto: followed by a valid email, e.g. mailto:support@yourcompany.com';
        return PLACEHOLDER_DOMAIN_RE.test(email.split('@')[1]) ? `${email.split('@')[1]} is a local/placeholder domain — push services need a real contact address` : null;
      }
      if (value.startsWith('https://')) {
        try {
          const host = new URL(value).hostname;
          return PLACEHOLDER_DOMAIN_RE.test(host) ? `${host} is a local/placeholder host — use your real website` : null;
        } catch {
          return 'Not a valid https:// URL';
        }
      }
      return 'Must start with mailto: or https://';
    }
    case 'vapid_public_key':
      return /^B[A-Za-z0-9_-]{86}$/.test(value)
        ? null
        : 'Not a VAPID public key — expected an 87-character base64url value starting with "B" (use Generate key pair)';
    case 'invoice_prefix': {
      const v = value.trim().toUpperCase();
      if (!/^[A-Z](?:[A-Z0-9]|-(?=[A-Z0-9])){1,9}$/.test(v)) {
        return 'Use 2–10 characters: letters, digits and single hyphens, starting with a letter and not ending with a hyphen (e.g. INV or ACME-INV)';
      }
      return RESERVED_DOCUMENT_PREFIXES.includes(v)
        ? `${v} is already used for other document numbers (${RESERVED_DOCUMENT_PREFIXES.join(', ')}) — choose a different prefix`
        : null;
    }
    case 's3_bucket':
      if (value.length < 3 || value.length > 63) return 'Bucket names are 3–63 characters long';
      if (!/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/.test(value)) return 'Use lowercase letters, digits, dots and hyphens, starting and ending with a letter or digit';
      if (/\.\.|\.-|-\./.test(value)) return 'Dots cannot be next to another dot or a hyphen';
      if (/^\d{1,3}(\.\d{1,3}){3}$/.test(value)) return 'A bucket name cannot look like an IP address';
      return null;
    case 's3_access_key_id':
      if (/[*•]{3,}/.test(value)) return null;
      if (/^ASIA[A-Z0-9]{16}$/.test(value)) return 'This is a temporary (STS) key — use a long-term IAM user access key (starts with AKIA)';
      return /^AKIA[A-Z0-9]{16}$/.test(value) ? null : 'An AWS access key ID is 20 characters: AKIA followed by 16 capital letters/digits';
    case 's3_secret_key':
      if (/[*•]{3,}/.test(value)) return null;
      return /^[A-Za-z0-9/+]{40}$/.test(value) ? null : 'An AWS secret access key is 40 characters (letters, digits, / and +)';
    case 's3_public_url':
      try {
        const u = new URL(value);
        if (u.protocol !== 'https:') return 'Use https:// — images served over http are blocked';
        if (u.search || u.hash) return 'Remove the ?query or #fragment — file paths are appended to this URL';
        return null;
      } catch {
        return 'Enter a full URL such as https://cdn.yourcompany.com';
      }
    case 'google_api_key':
      return /^AIza[0-9A-Za-z_-]{35}$/.test(value) ? null : 'A Google API key is 39 characters starting with "AIza"';
    case 'socket_url': {
      let u: URL;
      try { u = new URL(value); } catch { return 'Enter a full URL such as wss://realtime.yourcompany.com'; }
      if (u.protocol === 'http:' || u.protocol === 'ws:') return 'Use https:// or wss:// — the mobile apps block unencrypted connections';
      if (u.protocol !== 'https:' && u.protocol !== 'wss:') return 'Only https:// and wss:// Socket.IO URLs are supported';
      if (u.search || u.hash) return 'Remove the ?query or #fragment';
      if (u.pathname && u.pathname !== '/') return 'Enter only the server address (no path) — the apps add /socket.io automatically';
      if (/^(localhost|127\.|0\.0\.0\.0|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(u.hostname)) return 'Use a public domain — phones cannot reach a local address';
      return null;
    }
    case 'ai_api_key':
      if (/\s/.test(value)) return 'API keys contain no spaces';
      return value.length >= 16 ? null : 'Too short to be an API key — copy the full secret key';
    case 'ai_endpoint': {
      let u: URL;
      try { u = new URL(value); } catch { return 'Enter a full URL such as https://api.groq.com/openai/v1'; }
      if (u.protocol !== 'https:') return 'Use https:// — the API key is sent with every request';
      if (u.search || u.hash) return 'Remove the ?query or #fragment';
      if (/\/chat\/completions\/?$/.test(u.pathname)) return 'Enter the base URL only — remove /chat/completions';
      return null;
    }
    case 'ai_model':
      return /^[A-Za-z0-9][A-Za-z0-9._:/@-]{1,99}$/.test(value) ? null : 'Enter the model ID exactly as the provider lists it, e.g. gpt-4o-mini';
    case 'analytics_id':
      if (/^UA-\d+-\d+$/i.test(value)) return 'Universal Analytics (UA-…) stopped collecting data in 2023 — use a GA4 Measurement ID (G-…)';
      return GA4_ID_RE.test(value.toUpperCase()) || MIXPANEL_TOKEN_RE.test(value.toLowerCase())
        ? null
        : 'Enter a GA4 Measurement ID (G-XXXXXXXXXX) or a Mixpanel project token (32 characters)';
    default:
      return null;
  }
}

const RESERVED_DOCUMENT_PREFIXES = ['SUB', 'FD', 'GR', 'OD', 'OQ', 'BK', 'QR'];

/** Mirrors the backend rules so problems show before Save. The server re-checks. */
function validateValue(def: SettingKeyDef, raw: string, ctx: SettingsContext | undefined, options: { value: string }[]): string | null {
  if (def.type === 'file' || def.type === 'image') return null;
  if (def.type === 'password' || def.isEncrypted) {
    const secret = raw.trim();
    return secret && !/[*•]{3,}/.test(secret) ? formatError(def.format, secret) : null;
  }
  const value = raw.trim();
  const v = def.validation || {};
  if (!value) return v.required ? `${def.label} is required` : null;
  if (v.minLength && value.length < v.minLength) return `At least ${v.minLength} characters`;
  if (v.maxLength && value.length > v.maxLength) return `${v.maxLength} characters or less`;
  const fmt = formatError(def.format, value);
  if (fmt) return fmt;
  switch (def.type) {
    case 'email':
      return EMAIL_RE.test(value) ? null : 'Enter a valid email address (e.g. support@yourcompany.com)';
    case 'url':
      if (def.format === 'socket_url') return null;
      return /^https?:\/\/[^\s]+\.[^\s]+/.test(value) ? null : 'Enter a full http(s) URL';
    case 'number': {
      const n = Number(value);
      if (!/^-?\d+(\.\d+)?$/.test(value) || !Number.isFinite(n)) return 'Must be a number';
      const unit = def.unit ? ` ${def.unit}` : '';
      if (v.min != null && n < v.min) return `Must be at least ${v.min}${unit}`;
      if (v.max != null && n > v.max) return `Must be at most ${v.max}${unit}`;
      if (v.integer && !Number.isInteger(n)) return 'Must be a whole number';
      if (v.step && n % v.step !== 0) return `Must be a multiple of ${v.step}${unit}`;
      if (def.money && ctx) {
        const decimals = tenantCurrency(ctx)?.decimalPlaces ?? 2;
        const given = value.includes('.') ? value.split('.')[1].length : 0;
        if (given > decimals) {
          return decimals === 0
            ? `${ctx.tenant.currency} amounts have no decimals`
            : `${ctx.tenant.currency} allows at most ${decimals} decimal place${decimals === 1 ? '' : 's'}`;
        }
      }
      return null;
    }
    case 'color':
      return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value) ? null : 'Hex colour like #173A5E';
    case 'json':
      try { JSON.parse(raw); return null; } catch { return 'Must be valid JSON'; }
    case 'phone': {
      const compact = value.replace(/[\s()-]/g, '');
      if (!/^\+?\d+$/.test(compact)) return 'Digits only, with an optional leading +';
      const dial = ctx?.country.dialCode || '';
      const dialDigits = dial.replace(/\D/g, '');
      const bounds = ctx?.country.phoneLength || { min: 6, max: 14 };
      let digits = compact.replace(/^\+/, '');
      if (!compact.startsWith('+') && dialDigits) {
        const carries = digits.startsWith(dialDigits)
          && digits.length - dialDigits.length >= bounds.min && digits.length - dialDigits.length <= bounds.max;
        if (!carries) digits = `${dialDigits}${digits}`;
      }
      if (dialDigits && !digits.startsWith(dialDigits)) return `Use the ${ctx?.country.name} dial code ${dial}`;
      const local = digits.slice(dialDigits.length);
      if (local.length < bounds.min || local.length > bounds.max) {
        const need = bounds.min === bounds.max ? `${bounds.min}` : `${bounds.min}–${bounds.max}`;
        return `${ctx?.country.name || 'This country'} numbers need ${need} digits after ${dial || 'the code'} (entered ${local.length})`;
      }
      return null;
    }
    case 'select':
      return options.length && !options.some((o) => o.value.toLowerCase() === value.toLowerCase())
        ? `"${value}" is not an available option`
        : null;
    default:
      return null;
  }
}

function formatDateSample(format: string, date = new Date()) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  return format.replace('DD', dd).replace('MM', mm).replace('YYYY', yyyy);
}

function formatMoneySample(c: SettingsContext['currencies'][number] | undefined, amount = 1234567.5) {
  if (!c) return '';
  const fixed = amount.toFixed(Math.max(0, Math.min(4, c.decimalPlaces)));
  const [int, frac] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, c.thousandSeparator);
  const num = frac ? `${grouped}${c.decimalSeparator}${frac}` : grouped;
  return c.position === 'after' ? `${num} ${c.symbol}` : `${c.symbol}${num}`;
}

function timeIn(tz: string) {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', weekday: 'short', timeZoneName: 'short' }).format(new Date());
  } catch {
    return '';
  }
}

/** Live hint under General fields so the effect of a value is visible before saving. */
function fieldPreview(def: SettingKeyDef, value: string, ctx: SettingsContext | undefined): string | null {
  if (!ctx) return null;
  switch (def.key) {
    case 'support_phone': {
      const len = ctx.country.phoneLength;
      const need = len ? (len.min === len.max ? `${len.min}` : `${len.min}–${len.max}`) : '';
      return `${ctx.country.name}: ${ctx.country.dialCode || 'international'} + ${need} digits · saved as ${ctx.country.dialCode || '+'}XXXXXXXXXX`;
    }
    case 'timezone':
      return value ? `Now in ${value}: ${timeIn(value)}` : null;
    case 'date_format':
      return value ? `Today displays as ${formatDateSample(value)}` : null;
    case 'currency_default': {
      const c = ctx.currencies.find((x) => x.value === value.toUpperCase());
      return c
        ? `Formats as ${formatMoneySample(c)} · ${c.decimalPlaces} decimals · symbol ${c.position} amount (from Currencies master)`
        : null;
    }
    case 'language_default': {
      const l = ctx.languages.find((x) => x.value === value.toLowerCase());
      if (!l) return null;
      return `Language "${l.value}" uses locale ${l.locale}${l.provisioned ? '' : ' · translations incomplete (see Translator Master)'}`;
    }
    case 'min_fare': {
      const c = tenantCurrency(ctx);
      const n = Number(value);
      if (!c || !Number.isFinite(n)) return null;
      return n === 0 ? 'No minimum — fares are charged as calculated' : `Rides cost at least ${formatMoneySample(c, n)} before discounts & tax`;
    }
    case 'driver_search_timeout_sec': {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return null;
      return `${n >= 60 ? `${Math.floor(n / 60)} min ${n % 60 ? `${n % 60} s` : ''}`.trim() : `${n} s`} · about ${Math.max(1, Math.floor(n / 30))} driver offer${n >= 60 ? 's' : ''} of 30 s each`;
    }
    case 'min_order_amount': {
      const c = tenantCurrency(ctx);
      const n = Number(value);
      if (!c || !Number.isFinite(n)) return null;
      return n === 0
        ? 'No platform minimum — only each store\'s own minimum applies'
        : `Carts below ${formatMoneySample(c, n)} cannot check out (a store's own higher minimum still wins)`;
    }
    case 'slot_duration_min': {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return null;
      const start = 9 * 60;
      const fmt = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
      return `Example slots: ${fmt(start)}–${fmt(start + n)}, ${fmt(start + n)}–${fmt(start + 2 * n)} · store slot windows must divide evenly`;
    }
    case 'refund_window_days': {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return null;
      const sample = new Date();
      sample.setDate(sample.getDate() + n);
      return `An order delivered today can be refunded until ${sample.toDateString()} · after that only a Super Admin override`;
    }
    case 'payout_hold_days': {
      const n = Number(value);
      if (!Number.isFinite(n) || n < 0) return null;
      if (n === 0) return 'Earnings are released as soon as the order or trip completes';
      const sample = new Date();
      sample.setDate(sample.getDate() + n);
      return `Earnings from an order completed today are released on ${sample.toDateString()}`;
    }
    case 'substitution_response_min': {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return null;
      return `If the customer doesn't answer within ${n} min, the item is removed and refunded`;
    }
    case 'grace_period_days': {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0) return null;
      return n === 0
        ? 'No grace period — a subscription is cancelled as soon as a renewal payment fails'
        : `After a failed renewal: Past Due for ${n} day${n === 1 ? '' : 's'} with daily retries, then cancelled if still unpaid`;
    }
    case 'trial_days': {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0) return null;
      return n === 0
        ? 'New plans have no trial — billing starts on the first day'
        : `New plans start with a ${n}-day free trial; the subscription becomes Active automatically on day ${n + 1}`;
    }
    case 'invoice_prefix': {
      if (formatError('invoice_prefix', value)) return null;
      const prefix = value.trim().toUpperCase();
      const next = `${prefix}-${String((ctx.subscription?.invoiceSequence ?? 0) + 1).padStart(6, '0')}`;
      const last = ctx.subscription?.invoices.lastNumber;
      return `Next invoice: ${next}${last ? ` · existing invoices keep their numbers (latest ${last})` : ''}`;
    }
    case 'points_expiry_days': {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0) return null;
      if (n === 0) return 'Points never expire';
      const sample = new Date();
      sample.setDate(sample.getDate() + n);
      return `Points earned today expire on ${sample.toDateString()} if not spent`;
    }
    case 'referral_bonus': {
      const c = tenantCurrency(ctx);
      const n = Number(value);
      if (!c || !Number.isFinite(n) || n < 0) return null;
      return n === 0
        ? 'Referral rewards are off — referral codes cannot be applied'
        : `Referrer and new customer each get ${formatMoneySample(c, n)} in their wallet when the new customer's first order is delivered`;
    }
    default:
      return null;
  }
}

function displayValue(def: SettingKeyDef | undefined, value: string) {
  if (def?.type === 'boolean') return value === 'true' ? 'Enabled' : 'Disabled';
  if (def?.unit && value) return `${value} ${def.unit}`;
  return value;
}

function rangeHint(def: SettingKeyDef, ctx: SettingsContext | undefined): string | null {
  if (def.type !== 'number') return null;
  const v = def.validation || {};
  if (v.min == null && v.max == null) return null;
  const unit = def.money ? ` ${ctx?.tenant.currency || ''}`.trimEnd() : def.unit ? ` ${def.unit}` : '';
  const range = v.min != null && v.max != null ? `${v.min}–${v.max}${unit}` : v.min != null ? `≥ ${v.min}${unit}` : `≤ ${v.max}${unit}`;
  const decimals = def.money ? tenantCurrency(ctx)?.decimalPlaces : undefined;
  const steps = v.step ? `, in steps of ${v.step}` : v.integer ? ', whole numbers' : '';
  return `Allowed ${range}${steps}${decimals != null ? `, up to ${decimals} decimals` : ''}`;
}

function SettingFieldInput({
  def, value, onChange, category, onFileUploaded, error, options, hint, fileInfo, storageMode, currencySymbol, currencyDecimals,
}: {
  currencySymbol?: string;
  currencyDecimals?: number;
  def: SettingKeyDef;
  value: string;
  onChange: (v: string) => void;
  category: string;
  onFileUploaded?: () => void;
  error?: string | null;
  options: { value: string; label: string }[];
  hint?: string | null;
  fileInfo?: SettingRow['fileInfo'];
  storageMode?: 'local' | 's3';
}) {
  const helper = error || [def.description, hint].filter(Boolean).join(' — ');
  if (def.type === 'file' || def.type === 'image') {
    return (
      <SettingFileUpload
        category={category}
        settingKey={def.key}
        label={def.label}
        description={def.description}
        value={value}
        accept={def.accept}
        rules={def.upload}
        fileInfo={fileInfo}
        storageMode={storageMode}
        onChange={onChange}
        onUploaded={() => onFileUploaded?.()}
        onRemoved={() => onFileUploaded?.()}
      />
    );
  }
  if (def.type === 'boolean') {
    const on = value === 'true';
    return (
      <Box>
        <FormControlLabel
          control={<Switch checked={on} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />}
          label={(
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>{def.label}</Typography>
              <Chip size="small" label={on ? 'Enabled' : 'Disabled'} color={on ? 'success' : 'default'} variant={on ? 'filled' : 'outlined'} />
            </Stack>
          )}
        />
        {def.description && (
          <Typography variant="caption" color="text.secondary" display="block">{def.description}</Typography>
        )}
      </Box>
    );
  }
  if (def.type === 'select') {
    const known = options.some((o) => o.value === value);
    return (
      <TextField select fullWidth label={def.label} value={value} onChange={(e) => onChange(e.target.value)} helperText={helper} error={!!error} size="small">
        {!known && value && <MenuItem value={value} disabled>{value} (not available)</MenuItem>}
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    );
  }
  if (def.type === 'color') {
    return (
      <TextField fullWidth label={def.label} value={value} onChange={(e) => onChange(e.target.value)} helperText={helper} error={!!error} size="small" type="color" InputLabelProps={{ shrink: true }} />
    );
  }
  return (
    <TextField
      fullWidth
      label={def.label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={helper}
      error={!!error}
      size="small"
      type={def.type === 'password' ? 'password' : def.type === 'number' ? 'number' : def.type === 'email' ? 'email' : def.type === 'phone' ? 'tel' : 'text'}
      inputProps={def.type === 'number' ? {
        min: def.validation?.min,
        max: def.validation?.max,
        step: def.validation?.step
          ?? (def.validation?.integer ? 1 : def.money ? 1 / 10 ** (currencyDecimals ?? 2) : 'any'),
        inputMode: def.validation?.integer ? 'numeric' : 'decimal',
      } : undefined}
      InputProps={def.isEncrypted
        ? { startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment> }
        : def.money && currencySymbol
          ? { startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }
          : def.unit
            ? { endAdornment: <InputAdornment position="end">{def.unit}</InputAdornment> }
            : undefined}
    />
  );
}

export default function SettingsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [catalogOpen, setCatalogOpen] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCategory = searchParams.get('category') || 'general';
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: catalogRaw = [] } = useQuery({ queryKey: ['settings-catalog'], queryFn: getSettingsCatalog });
  const catalog = useMemo(
    () => (catalogRaw as SettingCategoryDef[]).filter((c) => c.key !== 'mobile_versioning'),
    [catalogRaw],
  );
  const { data: settings = [] } = useQuery({ queryKey: ['settings'], queryFn: () => getSettings() });
  const { data: ctxData } = useQuery({ queryKey: ['settings-context'], queryFn: getSettingsContext });
  const ctx = ctxData?.context;

  const activeCategory = useMemo(() => {
    if (!catalog.length) return requestedCategory;
    return catalog.some((c: SettingCategoryDef) => c.key === requestedCategory)
      ? requestedCategory
      : 'general';
  }, [catalog, requestedCategory]);

  useEffect(() => {
    if (catalog.length && requestedCategory !== activeCategory) {
      setSearchParams({ category: activeCategory }, { replace: true });
    }
  }, [catalog.length, requestedCategory, activeCategory, setSearchParams]);

  const categoryDef = useMemo(
    () => catalog.find((c: SettingCategoryDef) => c.key === activeCategory),
    [catalog, activeCategory]
  );

  const settingsMap = useMemo(() => {
    const map = new Map<string, SettingRow>();
    settings.forEach((s: SettingRow) => map.set(`${s.category}.${s.key}`, s));
    return map;
  }, [settings]);

  const defsById = useMemo(() => {
    const map = new Map<string, SettingKeyDef>();
    catalog.forEach((c) => c.keys.forEach((k) => map.set(`${c.key}.${k.key}`, k)));
    return map;
  }, [catalog]);

  const optionsFor = (category: string, def: SettingKeyDef) =>
    ctxData?.options?.[`${category}.${def.key}`] || def.options || [];

  const savedValue = (category: string, def: SettingKeyDef) => {
    const stored = settingsMap.get(`${category}.${def.key}`);
    return String(stored?.value ?? def.defaultValue ?? '');
  };

  const getValue = (category: string, def: SettingKeyDef) => {
    const id = `${category}.${def.key}`;
    return draft[id] !== undefined ? draft[id] : savedValue(category, def);
  };

  const setValue = (category: string, def: SettingKeyDef, value: string) => {
    const id = `${category}.${def.key}`;
    setServerErrors((p) => {
      if (!p[id]) return p;
      const next = { ...p };
      delete next[id];
      return next;
    });
    setDraft((p) => {
      const next = { ...p };
      // Typing the saved value back is not an unsaved change
      if (value === savedValue(category, def)) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const draftErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    Object.entries(draft).forEach(([id, value]) => {
      const def = defsById.get(id);
      if (!def) return;
      const msg = validateValue(def, value, ctx, ctxData?.options?.[id] || def.options || []);
      if (msg) errs[id] = msg;
    });
    // Cross-key rules only block when an involved key has an unsaved change
    const effective: Record<string, string> = {};
    settings.forEach((s: SettingRow) => { effective[`${s.category}.${s.key}`] = String(s.value ?? ''); });
    Object.assign(effective, draft);
    const cross = crossFieldErrors(effective);
    Object.entries(cross).forEach(([id, msg]) => { if (draft[id] !== undefined && !errs[id]) errs[id] = msg; });
    return { ...errs, ...serverErrors };
  }, [draft, defsById, ctx, ctxData, serverErrors, settings]);

  // "Configured" = saved for this tenant, non-empty and valid. Only catalog keys count.
  const statsByCategory = useMemo(() => {
    const stats: Record<string, { configured: number; attention: number; total: number }> = {};
    catalog.forEach((c) => {
      let configured = 0;
      let attention = 0;
      c.keys.forEach((k) => {
        const row = settingsMap.get(`${c.key}.${k.key}`);
        if (row?.inactive) return;
        if ((row?.hasValue && row.issue) || row?.warning || row?.advisory) attention += 1;
        else if (row?.hasValue) configured += 1;
      });
      stats[c.key] = { configured, attention, total: c.keys.length };
    });
    return stats;
  }, [catalog, settingsMap]);
  const totals = useMemo(
    () => Object.values(statsByCategory).reduce(
      (acc, s) => ({ configured: acc.configured + s.configured, attention: acc.attention + s.attention, total: acc.total + s.total }),
      { configured: 0, attention: 0, total: 0 },
    ),
    [statsByCategory],
  );

  const pendingChanges = useMemo(() => Object.entries(draft).map(([id, to]) => {
    const def = defsById.get(id);
    const [category, ...rest] = id.split('.');
    const key = rest.join('.');
    const cat = catalog.find((c) => c.key === category);
    return {
      id,
      category,
      key,
      categoryLabel: cat?.label || category,
      label: def?.label || key,
      from: String(settingsMap.get(id)?.value ?? def?.defaultValue ?? ''),
      to,
      fromLabel: displayValue(def, String(settingsMap.get(id)?.value ?? def?.defaultValue ?? '')),
      toLabel: displayValue(def, to),
      secret: !!def?.isEncrypted || def?.type === 'password',
    };
  }), [draft, defsById, catalog, settingsMap]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const items = pendingChanges.map((c) => ({ category: c.category, key: c.key, value: c.to }));
      if (!items.length) return [];
      return bulkUpsertSettings(items);
    },
    onSuccess: () => {
      const count = pendingChanges.length;
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['settings-context'] });
      queryClient.invalidateQueries({ queryKey: ['settings-history'] });
      setDraft({});
      void refreshTenantSession(dispatch);
      setSnack('Settings saved successfully');
    },
  });

  const historyCategory = ['security', 'partner_onboarding', 'locale'].includes(activeCategory) ? activeCategory : '';
  const { data: categoryHistory = [] } = useQuery({
    queryKey: ['settings-history', historyCategory],
    queryFn: () => getSettingsHistory(historyCategory, 5),
    enabled: !!historyCategory,
  });
  const securityHistory = activeCategory === 'security' ? categoryHistory : [];
  const [storageTest, setStorageTest] = useState<StorageTestResult | null>(null);
  const storageTestMutation = useMutation({
    mutationFn: testStorageConnection,
    onSuccess: (data) => {
      setStorageTest(data);
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['settings-context'] });
      setSnack({ severity: data.ok ? 'success' : 'error', message: data.ok ? 'S3 connection verified' : `S3 test failed: ${data.steps.find((s) => !s.ok)?.message || ''}` });
    },
    onError: (err: unknown) => {
      setStorageTest(null);
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSnack({ severity: 'error', message: message || 'S3 connection test failed' });
    },
  });
  const [aiTest, setAiTest] = useState<AiTestResult | null>(null);
  const aiTestMutation = useMutation({
    mutationFn: testAiConnection,
    onSuccess: (data) => {
      setAiTest(data);
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['settings-context'] });
      setSnack({ severity: data.ok ? 'success' : 'error', message: data.ok ? 'AI connection verified' : `AI test failed: ${data.steps.find((s) => !s.ok)?.message || ''}` });
    },
    onError: (err: unknown) => {
      setAiTest(null);
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSnack({ severity: 'error', message: message || 'AI connection test failed' });
    },
  });
  const [integrationTests, setIntegrationTests] = useState<Partial<Record<IntegrationTestKey, IntegrationTestResult>>>({});
  const integrationTestMutation = useMutation({
    mutationFn: ({ key, value }: { key: IntegrationTestKey; value?: string }) => testIntegrationSetting(key, value),
    onSuccess: (data) => {
      setIntegrationTests((prev) => ({ ...prev, [data.key]: data }));
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['settings-context'] });
      const failed = data.steps.find((s) => !s.ok);
      setSnack({ severity: data.ok ? 'success' : 'error', message: data.ok ? 'Test passed' : `Test failed: ${failed?.message || ''}` });
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSnack({ severity: 'error', message: message || 'Test failed' });
    },
  });
  const [pushTest, setPushTest] = useState<PushTestResult | null>(null);
  const pushTestMutation = useMutation({
    mutationFn: runPushTest,
    onSuccess: (data) => setPushTest(data),
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSnack({ severity: 'error', message: message || 'Push test failed' });
    },
  });
  const generateKeysMutation = useMutation({
    mutationFn: generateVapidKeys,
    onSuccess: (keys) => {
      setDraft((p) => ({
        ...p,
        'notification.web_push_vapid_public_key': keys.publicKey,
        'notification.web_push_vapid_private_key': keys.privateKey,
      }));
      setSnack({ severity: 'success', message: 'New VAPID key pair filled in — save to apply. Browsers subscribed with the old key must re-allow notifications.' });
    },
    onError: () => setSnack({ severity: 'error', message: 'Could not generate a VAPID key pair' }),
  });

  const dirtyCount = Object.keys(draft).length;
  const errorCount = Object.keys(draftErrors).length;
  const categoryDraftCount = categoryDef?.keys.filter((k: SettingKeyDef) => draft[`${activeCategory}.${k.key}`] !== undefined).length || 0;
  const activeStats = statsByCategory[activeCategory] || { configured: 0, attention: 0, total: categoryDef?.keys.length || 0 };

  const categoryList = (
    <>
      <Box sx={{ p: 2, background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`, color: 'white' }}>
        <Typography variant="subtitle1" fontWeight={700}>Settings Catalog</Typography>
        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
          {totals.configured} of {totals.total} keys configured
          {totals.attention ? ` · ${totals.attention} need attention` : ''} · the rest use defaults
        </Typography>
        {ctxData?.tenant && (
          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
            Tenant: {ctxData.tenant.name} ({ctxData.tenant.slug})
          </Typography>
        )}
      </Box>
      <List dense disablePadding>
        {catalog.map((cat: SettingCategoryDef) => {
          const s = statsByCategory[cat.key] || { configured: 0, attention: 0, total: cat.keys.length };
          const unsaved = cat.keys.filter((k) => draft[`${cat.key}.${k.key}`] !== undefined).length;
          return (
            <ListItemButton
              key={cat.key}
              selected={activeCategory === cat.key}
              onClick={() => {
                setSearchParams({ category: cat.key });
                setCatalogOpen(false);
              }}
              sx={{ borderLeft: activeCategory === cat.key ? 3 : 0, borderColor: 'secondary.main' }}
            >
              <Box mr={1} color="secondary.main">{CATEGORY_ICONS[cat.key] || <Tune fontSize="small" />}</Box>
              <ListItemText
                primary={cat.label}
                secondary={`${s.configured}/${s.total} configured${s.attention ? ` · ${s.attention} to fix` : ''}${unsaved ? ` · ${unsaved} unsaved` : ''}`}
                primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 'calc(100vh - 180px)' }}>
      {isMobile ? (
        <>
          <Button variant="outlined" startIcon={<Menu />} onClick={() => setCatalogOpen(true)} sx={{ alignSelf: 'flex-start' }}>
            {categoryDef?.label || 'Settings'}
          </Button>
          <Drawer anchor="left" open={catalogOpen} onClose={() => setCatalogOpen(false)}>
            <Box sx={{ width: 280 }}>{categoryList}</Box>
          </Drawer>
        </>
      ) : (
        <Paper elevation={0} sx={{ width: 280, flexShrink: 0, borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          {categoryList}
        </Paper>
      )}

      <Box flex={1} minWidth={0}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, border: 1, borderColor: 'divider', background: alpha(theme.palette.secondary.main, 0.04) }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={800}>{categoryDef?.label || activeCategory}</Typography>
              <Typography variant="body2" color="text.secondary">{categoryDef?.description}</Typography>
              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Chip size="small" label={`${activeStats.configured}/${activeStats.total} configured`} />
                {activeStats.attention > 0 && <Chip size="small" color="error" variant="outlined" label={`${activeStats.attention} need attention`} />}
                {categoryDraftCount > 0 && <Chip size="small" color="warning" label={`${categoryDraftCount} unsaved here`} />}
                {dirtyCount > categoryDraftCount && <Chip size="small" color="warning" variant="outlined" label={`${dirtyCount - categoryDraftCount} unsaved in other categories`} />}
              </Box>
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
              {dirtyCount > 0 && (
                <Button startIcon={<Undo />} onClick={() => { setDraft({}); setServerErrors({}); }} disabled={saveMutation.isPending}>
                  Discard
                </Button>
              )}
              <Tooltip title={dirtyCount === 0 ? 'No unsaved changes' : errorCount ? `Fix ${errorCount} invalid value(s) first` : ''}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Save />}
                    disabled={dirtyCount === 0 || errorCount > 0 || saveMutation.isPending}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save Changes'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: 1, borderColor: 'divider' }}>
          {activeCategory === 'storage' && ctx?.storage && (() => {
            const s = ctx.storage;
            const v = s.verification;
            const enabledDraft = draft['storage.s3_enabled'];
            const switching = enabledDraft !== undefined || ['s3_bucket_name', 's3_region', 's3_public_url_base'].some((k) => draft[`storage.${k}`] !== undefined);
            const routing = s.provider === 's3'
              ? `Amazon S3 — bucket ${s.bucket} (${s.region}), under tenants/<tenant id>/`
              : s.s3Incomplete ? 'API server (S3 is ON but incomplete, so uploads fall back to the server)' : 'API server (local storage)';
            return (
              <Alert severity={s.s3Incomplete || (s.enabled && (!v?.current || !v.ok)) ? 'warning' : 'info'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Chip size="small" color={s.provider === 's3' ? 'success' : 'default'} label={`New uploads go to: ${routing}`} />
                  {s.enabled && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={v?.current && v.ok ? 'success' : 'warning'}
                      label={!v ? 'Connection: never tested' : !v.current ? 'Connection: not tested since settings changed' : v.ok ? `Connection verified ${new Date(v.at).toLocaleString()}` : `Connection failed ${new Date(v.at).toLocaleString()}`}
                    />
                  )}
                  <Chip size="small" variant="outlined" label={`Files on the API server: ${s.localFiles ?? 0}${(s.localFiles ?? 0) >= 10000 ? '+' : ''}`} />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={storageTestMutation.isPending}
                    startIcon={storageTestMutation.isPending ? <CircularProgress size={14} /> : undefined}
                    onClick={() => {
                      const values: Record<string, string> = {};
                      categoryDef?.keys.forEach((k) => { if (k.key !== 's3_enabled') values[k.key] = getValue('storage', k); });
                      storageTestMutation.mutate(values);
                    }}
                  >
                    Test connection
                  </Button>
                </Box>
                {storageTest && (
                  <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                    {storageTest.steps.map((st) => (
                      <li key={st.step}>
                        <Typography variant="caption" color={st.ok ? 'success.main' : 'error.main'}>
                          {st.ok ? '✓' : '✗'} {st.message}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
                {!storageTest && v && !v.ok && v.current && (
                  <Typography variant="caption" display="block" color="error.main" sx={{ mb: 1 }}>Last test: {v.message}</Typography>
                )}
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li><strong>OFF:</strong> files are saved on the API server under uploads/tenants/&lt;tenant id&gt;/, and the S3 fields are not used (shown as &ldquo;Not in use&rdquo;, saved values are kept).</li>
                  <li><strong>ON:</strong> bucket, region and both keys are required, and saving runs the connection test automatically. It checks the bucket exists in the selected region, the keys can upload, a file opens through the public URL, and then deletes the test file. S3 is only switched on if every check passes.</li>
                  <li><strong>Existing files are never moved or deleted.</strong> Files already on the server or in a previous bucket keep their links, so keep the old bucket and the server&apos;s uploads folder. Re-upload a file if you want it in the new location.</li>
                  <li>Each tenant uses only its own bucket, keys and tenants/&lt;tenant id&gt;/ folder. Keys are stored encrypted and never shown again.</li>
                  <li>Changes apply <strong>immediately to new uploads</strong>; nothing already uploaded changes.</li>
                </Box>
                {switching && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600 }}>
                    {enabledDraft === 'false'
                      ? 'You are turning S3 off: new uploads will go to the API server. Files already in S3 stay there and keep working.'
                      : 'You are changing where new uploads go. Existing files keep their current links; nothing is migrated.'}
                  </Typography>
                )}
              </Alert>
            );
          })()}
          {activeCategory === 'branding' && ctx?.storage && (
            <Alert severity={ctx.storage.s3Incomplete ? 'warning' : 'info'} sx={{ mb: 2 }}>
              <strong>Current storage: {ctx.storage.provider === 's3' ? `Amazon S3 (${ctx.storage.bucket}, ${ctx.storage.region})` : 'Local server'}</strong>
              {' — '}
              {ctx.storage.s3Incomplete
                ? 'S3 is switched on but its credentials are incomplete, so uploads still go to the local server. Complete them under File Storage.'
                : ctx.storage.provider === 's3'
                  ? 'new uploads are saved to this bucket.'
                  : 'new uploads are saved on the API server. Enable S3 under File Storage to use your own bucket.'}
            </Alert>
          )}
          {activeCategory === 'branding' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Uploads and removals apply immediately; colours and app name apply when you save. Branding is delivered to
              {' '}<strong>Admin panel</strong> (sidebar logo, browser favicon, login), and the
              {' '}<strong>Customer, Partner, Vendor and Provider apps</strong> through the mobile app config&apos;s <code>branding</code> block.
            </Alert>
          )}
          {activeCategory === 'transport' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              These are the tenant defaults the ride flow enforces for new bookings. Drivers get 30 s per offer, so
              the Search Timeout must be at least 4 s per km of Search Radius (minimum 60 s). Max Stops, Schedule Buffer and
              Pool Rides are checked on every booking, both in the customer app and in admin/dispatcher booking. Zone or zone-bidding radius and timeout settings override these defaults.
            </Alert>
          )}
          {activeCategory === 'food' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Order timeline: an online-payment order that stays unpaid is cancelled after the <strong>Unpaid Order Timeout</strong> (counted from checkout).
              Once paid (or immediately for cash), the restaurant is notified. With <strong>Auto Confirm OFF</strong>, it must accept within the
              {' '}<strong>Merchant Accept Timeout</strong> (counted from that notification; scheduled orders start counting when they are released to the restaurant), or the order is cancelled and refunded.
              With Auto Confirm ON, orders are confirmed instantly and the accept timeout is not used. The two timers never run at the same time.
              Min Order Amount and Max Delivery KM are checked at checkout; a restaurant&apos;s own higher minimum still applies.
            </Alert>
          )}
          {activeCategory === 'grocery' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Store delivery-slot windows must split evenly into slots of the <strong>Slot Duration</strong>, and the slot saved on scheduled orders spans that length.
              When <strong>Allow Substitutions</strong> is OFF, the per-item &ldquo;allow substitute&rdquo; option is ignored and unavailable items are removed and refunded.
              When ON, the approval mode, price rule and response time below are saved on each order for the store team and sent to the apps.
            </Alert>
          )}
          {activeCategory === 'payment' && ctx?.payments && (() => {
            const usable = ctx.payments.gateways.filter((g) => g.enabled && g.configured && g.supportsManualCapture);
            return (
              <Alert severity={usable.length ? 'info' : 'warning'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {ctx.payments.gateways.map((g) => (
                    <Chip
                      key={g.type}
                      size="small"
                      color={g.enabled && g.configured ? 'success' : 'default'}
                      variant={g.enabled && g.configured ? 'filled' : 'outlined'}
                      label={`${g.label} (${g.env === 'live' ? 'Live' : 'Sandbox'}): ${
                        !g.enabled ? 'disabled' : g.configured ? 'ready · auto & manual capture' : `incomplete — ${g.issues[0] || 'check credentials'}`
                      }`}
                    />
                  ))}
                </Box>
                {usable.length
                  ? <>Auto Capture applies to checkout on {usable.map((g) => g.label).join(' and ')}.</>
                  : <>No capture-capable gateway is enabled and configured, so Auto Capture has no effect. Set up Razorpay or Stripe under Payment Gateways.</>}
                {' '}With Auto Capture OFF, food and grocery payments are held at checkout and charged when the restaurant/store accepts; cancelled orders are released, never charged.
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                  <li>
                    <strong>Refunds:</strong> captured online payments are refunded to the card/UPI, cash and wallet payments to the customer wallet.
                    Uncaptured (authorized) payments are released instead, at any time. Failed or pending payments have nothing to refund, so cancel the order.
                  </li>
                  <li>
                    <strong>Payout Hold → Settlements:</strong> partner earnings show as Pending with <em>Held until &lt;date&gt;</em> on the{' '}
                    <Link component={RouterLink} to="/settlements">Settlements</Link> page until the hold ends,
                    then go to the partner wallet or the next payout cycle. Approve and Mark Paid are blocked while held, and a refund during the hold puts the settlement <em>On hold</em>.
                    Set it at least as long as the Refund Window.
                  </li>
                  <li>
                    <strong>Wallet:</strong>{' '}
                    {ctx.payments.wallet?.featureEnabled && ctx.payments.wallet?.gatewayEnabled
                      ? <>enabled, so Min Wallet Top-up ({ctx.tenant.currency}) is enforced on every customer top-up.</>
                      : <>{!ctx.payments.wallet?.featureEnabled ? 'the Wallet feature is off' : 'the Wallet payment method is disabled'} for this tenant, so Min Wallet Top-up is not used.</>}
                  </li>
                </Box>
              </Alert>
            );
          })()}
          {activeCategory === 'notification' && ctx?.notifications && (() => {
            const n = ctx.notifications;
            const pushReady = n.fcm.mode === 'v1' || n.webPush.ready;
            const fcmLabel = n.fcm.mode === 'v1'
              ? 'Firebase service account (FCM HTTP v1) — ready'
              : n.fcm.mode === 'legacy'
                ? (n.fcm.serverKeyIssue ? `legacy server key — ${n.fcm.serverKeyIssue}` : 'legacy server key — Google has retired this API, move to a service account')
                : 'not configured — mobile app push is off';
            const webLabel = n.webPush.ready
              ? `ready (${n.webPush.source === 'env' ? 'server environment keys' : 'keys below'})`
              : n.webPush.publicKeyStored || n.webPush.privateKeyStored
                ? (n.webPush.privateKeyIssue || 'keys incomplete or not a matching pair')
                : 'not configured — use Generate key pair';
            const toggleLabels: Record<string, string> = {
              push_on_ride_booked: 'Ride Booked',
              push_on_order_placed: 'Order Placed',
              push_on_delivery_assigned: 'Delivery Assigned',
            };
            const statusColor = (s: string) => (s === 'ok' ? 'success' : s === 'error' ? 'error' : s === 'warning' ? 'warning' : 'default') as 'success' | 'error' | 'warning' | 'default';
            return (
              <Alert severity={pushReady ? 'info' : 'warning'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip size="small" color={n.fcm.mode === 'v1' ? 'success' : n.fcm.mode === 'legacy' ? 'warning' : 'default'} variant="outlined" label={`FCM: ${fcmLabel}`} />
                  <Chip size="small" color={n.webPush.ready ? 'success' : n.webPush.publicKeyStored || n.webPush.privateKeyStored ? 'error' : 'default'} variant="outlined" label={`Web Push: ${webLabel}`} />
                  <Chip size="small" color={n.smtpConfigured ? 'success' : 'default'} variant="outlined" label={`Email (SMTP): ${n.smtpConfigured ? 'configured on server' : 'not configured — emails are only logged'}`} />
                </Box>
                <Typography variant="body2">
                  Push subject in use: <strong>{n.webPush.subject}</strong>
                  {formatError('push_subject', n.webPush.subject) ? ` (fallback — ${formatError('push_subject', n.webPush.subject)}; set Web Push Subject or the General support email)` : ''}.
                  {' '}Event templates:{' '}
                  {Object.entries(n.templates).map(([toggle, list], i) => {
                    const active = list.filter((t) => t.active).length;
                    return (
                      <span key={toggle}>
                        {i > 0 && ' · '}
                        {toggleLabels[toggle] || toggle}: {active ? `${active}/${list.length} active` : <strong>no active template</strong>}
                      </span>
                    );
                  })}
                  {' '}(manage under Notification Templates).
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Push Enabled controls customer push; partner job offers and merchant order alerts are always sent. In-app popups are the admin panel toasts for each event.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" onClick={() => generateKeysMutation.mutate()} disabled={generateKeysMutation.isPending}>
                    Generate key pair
                  </Button>
                  <Tooltip title={dirtyCount ? 'Save your changes first — the test uses saved settings' : 'Verify FCM and Web Push credentials and send a test push to you'}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => pushTestMutation.mutate()}
                        disabled={pushTestMutation.isPending || dirtyCount > 0}
                        startIcon={pushTestMutation.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
                      >
                        Test / verify push
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                {pushTest && (
                  <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                    {pushTest.checks.map((c) => (
                      <Box key={`${c.provider}-${c.message}`} display="flex" gap={1} alignItems="center">
                        <Chip size="small" color={statusColor(c.status)} label={`${c.provider === 'fcm' ? 'FCM' : c.provider === 'web_push' ? 'Web Push' : 'Delivery'}: ${c.status}`} />
                        <Typography variant="caption">{c.message}</Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Alert>
            );
          })()}
          {activeCategory === 'security' && ctx?.security && (() => {
            const s = ctx.security;
            const d = s.codeDelivery;
            const delivery = d.smtp && d.smsLive ? 'email (SMTP) and SMS' : d.smtp ? 'email (SMTP)' : d.smsLive ? 'SMS' : d.devOtp ? 'not delivered — dev OTP mode (fixed code)' : 'unavailable';
            const typeLabel = (t: string) => t.replace(/_/g, ' ');
            const affected = Object.entries(s.emailVerification.byType).map(([t, n]) => `${n} ${typeLabel(t)}`).join(', ');
            const admins = Object.entries(s.admins.byType).map(([t, n]) => `${n} ${typeLabel(t)}`).join(', ');
            return (
              <Alert severity={d.smtp || d.smsLive ? 'info' : 'warning'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip size="small" variant="outlined" color={d.smtp || d.smsLive ? 'success' : 'warning'} label={`One-time codes: ${delivery}`} />
                  <Chip size="small" variant="outlined" color={s.lockedAccounts ? 'warning' : 'default'} label={`Locked accounts now: ${s.lockedAccounts}`} />
                  <Chip size="small" variant="outlined" label={`Admin panel accounts: ${admins || 'none'}`} />
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li><strong>Session Timeout</strong> signs admin panel users out after inactivity, with a 1-minute warning. Open sessions pick up a new value within 15 minutes (at their next token renewal); mobile app sessions are not affected.</li>
                  <li><strong>Lockout:</strong> after Max Login Attempts wrong passwords the account is locked for the Lockout Duration and the user sees when to retry. Resetting the password unlocks it. Each IP is also limited to 20 sign-in attempts per 15 minutes.</li>
                  <li><strong>OTP:</strong> each code expires after OTP Expiry, works once, and is cancelled by a newer code or after OTP Max Attempts wrong entries. Code requests are limited to 3 per minute and checks to 10 per minute per IP.</li>
                  <li>
                    <strong>Email Verification</strong> applies to email + password sign-in for customer and partner accounts.
                    {' '}{s.emailVerification.affectedAccounts
                      ? <>If ON, <strong>{s.emailVerification.affectedAccounts}</strong> account(s) ({affected}) will have to enter an emailed code at their next sign-in.</>
                      : <>No current accounts would be affected.</>}
                    {' '}Phone OTP sign-in and admin staff are not affected.
                  </li>
                  <li><strong>MFA</strong> adds an emailed (or SMS) code after the password for the roles in MFA Required For. {s.admins.withoutContact ? <strong>{s.admins.withoutContact} admin account(s) have no email or phone and could not sign in with MFA on. </strong> : ''}</li>
                  <li>All changes apply to <strong>new sign-ins immediately</strong>; people already signed in are not logged out. Lockouts, MFA challenges and idle sign-outs are written to the audit log.</li>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Recent security changes</Typography>
                {securityHistory.length ? (
                  <Stack spacing={0.25}>
                    {securityHistory.map((h) => (
                      <Typography key={h.id} variant="caption">
                        {new Date(h.at).toLocaleString()} · {h.by}{h.ip ? ` (${h.ip})` : ''}:{' '}
                        {h.changes.map((c) => `${c.label} ${c.from ?? 'default'} → ${c.to}`).join('; ') || 'settings updated'}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" display="block">No security changes saved for this tenant yet.</Typography>
                )}
                <Link component={RouterLink} to="/audit-logs" variant="caption">Open the full audit log</Link>
              </Alert>
            );
          })()}
          {activeCategory === 'loyalty' && ctx?.loyalty && (() => {
            const l = ctx.loyalty;
            const c = tenantCurrency(ctx);
            return (
              <Alert severity={l.moduleEnabled && l.activeRules ? 'info' : 'warning'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip size="small" variant="outlined" color={l.moduleEnabled ? 'success' : 'warning'} label={`Loyalty module: ${l.moduleEnabled ? 'on' : 'off'}`} />
                  <Chip size="small" variant="outlined" color={l.activeRules ? 'default' : 'warning'} label={`Active earning rules: ${l.activeRules}`} />
                  <Chip size="small" variant="outlined" label={`Referrals: ${l.referrals.pending} pending · ${l.referrals.completed} rewarded`} />
                  <Chip size="small" variant="outlined" label={`Currency: ${ctx.tenant.currency}${c ? ` (${c.symbol}, ${c.decimalPlaces} decimals)` : ''}`} />
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li><strong>Points Expiry:</strong> points expire this many days after they are earned or awarded; <strong>0 = never expire</strong>. Points closest to expiry are spent first, only unspent points expire (checked hourly), and a balance never goes below zero. Expired points appear as &ldquo;Expired&rdquo; in the customer&apos;s points history.</li>
                  <li><strong>Referral Bonus</strong> is a wallet credit in {ctx.tenant.currency}, paid to both the referrer and the new customer once the new customer&apos;s <strong>{l.qualifyingEvent}</strong>. It is paid once per referred customer; a code can&apos;t be used by its owner, by an account with the same phone or email, or after the customer&apos;s first order. 0 turns referral rewards off.</li>
                  <li>Changes apply <strong>immediately</strong>: a new Points Expiry applies to points earned from now on (existing points keep their date), and a new Referral Bonus applies to codes applied from now on (pending referrals keep the amount promised when the code was applied).</li>
                  <li>Earning and redemption rates are set per module under <strong>Loyalty → Rules</strong>.</li>
                </Box>
              </Alert>
            );
          })()}
          {activeCategory === 'subscription' && ctx?.subscription && (() => {
            const s = ctx.subscription;
            return (
              <Alert severity={s.moduleEnabled ? 'info' : 'warning'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip size="small" variant="outlined" color={s.moduleEnabled ? 'success' : 'warning'} label={`Subscriptions module: ${s.moduleEnabled ? 'on' : 'off'}`} />
                  <Chip size="small" variant="outlined" label={`Active plans: ${s.plans.active} (${s.plans.withTrial} with trial)`} />
                  <Chip size="small" variant="outlined" color={s.subscriptions.pastDue ? 'warning' : 'default'} label={`Subscriptions: ${s.subscriptions.trialing} trialing · ${s.subscriptions.active} active · ${s.subscriptions.pastDue} past due`} />
                  <Chip size="small" variant="outlined" label={`Invoices issued: ${s.invoices.total}${s.invoices.lastNumber ? ` (latest ${s.invoices.lastNumber})` : ''}`} />
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li><strong>Trial:</strong> a new plan starts with the Default Trial unless you set its own Trial Days. A trial is free, needs no payment method and is given once per customer. When it ends the subscription becomes <strong>Active</strong> automatically and the first paid period starts.</li>
                  <li><strong>Billing:</strong> at the end of each period an invoice (base fee, usage overage and tax) is charged to the customer&apos;s wallet. An hourly job handles trial ends, renewals and retries.</li>
                  <li><strong>Failed payment:</strong> the subscription becomes <strong>Past Due</strong> and keeps its benefits for the Grace Period, with a retry every day; paying the invoice makes it Active again. If it&apos;s still unpaid when the grace period ends it is <strong>cancelled</strong>. A Grace Period of 0 cancels immediately.</li>
                  <li><strong>Invoice numbers</strong> are the prefix plus a tenant-wide sequence (e.g. INV-000042). The sequence never restarts, so changing the prefix can&apos;t create duplicates, and issued invoices keep their numbers.</li>
                  <li>Changes apply <strong>immediately, to new events only</strong>. A new Default Trial affects plans created from now on, not existing plans or subscriptions. A new Grace Period affects payments that fail from now on; subscriptions already past due keep their end date. A new prefix is used from the next invoice.</li>
                </Box>
              </Alert>
            );
          })()}
          {activeCategory === 'partner_onboarding' && ctx?.partnerOnboarding && (() => {
            const types = ctx.partnerOnboarding.partnerTypes;
            const approvalDef = defsById.get('partner_onboarding.document_approval_required');
            const maskDef = defsById.get('partner_onboarding.mask_email_and_phone');
            const approvalOn = approvalDef ? getValue('partner_onboarding', approvalDef) !== 'false' : true;
            const maskOn = maskDef ? getValue('partner_onboarding', maskDef) !== 'false' : true;
            const uncovered = types.filter((t) => !t.mandatoryDocuments.length);
            return (
              <Alert severity={approvalOn && !uncovered.length ? 'info' : 'warning'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip size="small" color={approvalOn ? 'success' : 'warning'} label={approvalOn ? 'Approval needs verified mandatory documents' : 'Partners can be approved without documents'} />
                  <Chip size="small" variant="outlined" color={maskOn ? 'success' : 'warning'} label={maskOn ? 'Lists & exports masked' : 'Lists & exports show full contacts'} />
                  <Chip size="small" variant="outlined" label={`Documents awaiting review: ${types.reduce((n, t) => n + t.pendingReview, 0)} · rejected awaiting re-upload: ${types.reduce((n, t) => n + t.rejected, 0)}`} />
                </Box>
                <Typography variant="subtitle2">Mandatory documents per partner type</Typography>
                <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                  {types.map((t) => (
                    <li key={t.entityType}>
                      <Typography variant="caption" color={t.mandatoryDocuments.length ? 'text.primary' : 'warning.main'}>
                        <strong>{t.label}:</strong>{' '}
                        {t.mandatoryDocuments.length ? t.mandatoryDocuments.join(', ') : 'none configured — approved without any document check'}
                        {t.optionalDocuments ? ` (+${t.optionalDocuments} optional)` : ''}
                        {t.pendingReview || t.rejected ? ` · ${t.pendingReview} awaiting review, ${t.rejected} rejected` : ''}
                      </Typography>
                    </li>
                  ))}
                </Box>
                <Link component={RouterLink} to="/config/documents" variant="caption">Configure mandatory documents (Config → Documents)</Link>
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                  <li><strong>Document Approval Required ON:</strong> a partner is approved and activated only when every mandatory document above is uploaded and verified. The active toggle, admin edits and unblocking can&apos;t activate an unapproved partner, and &quot;auto-verify&quot; at creation is refused for partner types with mandatory documents. OFF: partners can be approved without documents (existing approvals are kept either way).</li>
                  <li><strong>Rejection &amp; re-upload:</strong> rejecting a document needs a reason. The partner is notified with it, the document shows as Rejected in their app, and re-uploading sends it back to the review queue as Pending. Expired documents must also be re-uploaded before they can be approved.</li>
                  <li>
                    <strong>Mask Email &amp; Mobile</strong> — where it applies:
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      <li><strong>Lists</strong> (users, partners, document review queues): masked by the server, so the full value never reaches the browser.</li>
                      <li><strong>Search</strong>: still matches the full email or number.</li>
                      <li><strong>CSV exports</strong> from those lists: masked the same way.</li>
                      <li><strong>Detail / edit screens</strong> for a single person: full values, for staff with access to that record. Masked values are never saved back over real ones.</li>
                      <li><strong>Reports &amp; dashboards</strong>: show totals only, no contact details.</li>
                    </Box>
                  </li>
                  <li>Both settings apply to <strong>this tenant only</strong> and take effect within a minute. Changes, document approvals/rejections, partner approvals and auto-verified partners are written to the audit log.</li>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Recent changes to these settings</Typography>
                {categoryHistory.length ? (
                  <Stack spacing={0.25}>
                    {categoryHistory.map((h) => (
                      <Typography key={h.id} variant="caption">
                        {new Date(h.at).toLocaleString()} · {h.by}{h.ip ? ` (${h.ip})` : ''}:{' '}
                        {h.changes.map((c) => `${c.label} ${c.from ?? 'default'} → ${c.to}`).join('; ') || 'settings updated'}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" display="block">No changes saved for this tenant yet.</Typography>
                )}
                <Link component={RouterLink} to="/audit-logs" variant="caption">Open the full audit log</Link>
              </Alert>
            );
          })()}
          {activeCategory === 'locale' && ctx?.locale && (() => {
            const value = (key: string) => {
              const def = defsById.get(`locale.${key}`);
              return def ? getValue('locale', def) : '';
            };
            return (
              <LocaleCurrencyPanel
                locale={ctx.locale}
                draft={{
                  base: value('base_currency'),
                  display: value('display_currency'),
                  settlement: value('settlement_currency'),
                  reporting: value('reporting_currency'),
                  autoConvert: value('auto_convert_currency') === 'true',
                  provider: value('fx_provider'),
                  historical: value('historical_fx_required') === 'true',
                }}
                history={categoryHistory}
                onNotify={(severity, message) => setSnack({ severity, message })}
              />
            );
          })()}
          {activeCategory === 'ai' && ctx?.ai && (() => {
            const a = ctx.ai;
            const v = a.verification;
            const o = a.ocr.last30Days;
            const providerLabel = a.provider === 'openai_compatible' ? `OpenAI-compatible (${a.endpoint})` : 'OpenAI';
            return (
              <Alert severity={a.enabled && (!v?.current || !v.ok) ? 'warning' : 'info'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Chip size="small" color={a.enabled ? 'success' : 'default'} label={a.enabled ? `AI on · ${providerLabel} · ${a.model}` : 'AI off — no data is sent to any AI service'} />
                  {a.enabled && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={v?.current && v.ok ? 'success' : 'warning'}
                      label={!v || !v.current ? 'Connection: not tested since the settings changed' : v.ok ? `Connection verified ${new Date(v.at).toLocaleString()}${v.visionOk ? ' · images supported' : v.visionOk === false ? ' · no image support' : ''}` : 'Connection test failed'}
                    />
                  )}
                  <Chip size="small" variant="outlined" label={`OCR ${a.ocrEnabled ? 'on' : 'off'} · last 30 days: ${o.done} read, ${o.failed} failed, ${o.skipped} not run`} />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={aiTestMutation.isPending}
                    startIcon={aiTestMutation.isPending ? <CircularProgress size={14} /> : undefined}
                    onClick={() => {
                      const values: Record<string, string> = {};
                      categoryDef?.keys.forEach((k) => { values[k.key] = getValue('ai', k); });
                      aiTestMutation.mutate(values);
                    }}
                  >
                    Test connection
                  </Button>
                </Box>
                {aiTest && (
                  <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                    {aiTest.steps.map((st) => (
                      <li key={st.step}>
                        <Typography variant="caption" color={!st.ok ? 'error.main' : st.warning ? 'warning.main' : 'success.main'}>
                          {!st.ok ? '✗' : st.warning ? '!' : '✓'} {st.message}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li><strong>Turning AI on</strong> needs an API key and model (and endpoint for OpenAI-compatible APIs). Saving runs the connection test: the key must be accepted and the model available to it. <strong>OCR</strong> additionally needs a model that accepts images.</li>
                  <li><strong>If the AI service fails</strong> (timeout, rate limit, provider error), the request is retried up to &quot;Retries on Failure&quot; times. After that the feature falls back to manual handling — nothing is blocked.</li>
                  <li><strong>If OCR fails</strong> (or is off, or the file is a PDF), the document stays in the partner document review queue, with &quot;Failed&quot; / &quot;Not run&quot; and the reason in its OCR column. Reviewers can retry OCR or type the number in manually there, then approve or reject as usual.</li>
                  <li><strong>Data sent to the AI provider:</strong> only the images of partner documents uploaded for verification (ID cards, licences — personal data), and only while AI and OCR are on. Customer data, orders, payments and locations are never sent. Fraud alerts and demand predictions are calculated on this server without AI.</li>
                  <li>The provider receives the data under its own terms — check its data-processing agreement, retention and data-residency before turning OCR on. Only the extracted fields are stored here unless &quot;Keep Full OCR Text&quot; is on. Settings and keys apply to this tenant only (keys stored encrypted).</li>
                </Box>
              </Alert>
            );
          })()}
          {activeCategory === 'integration' && ctx?.integration && (() => {
            const i = ctx.integration;
            const apiOrigin = new URL(import.meta.env.VITE_API_URL || '/api/v1', window.location.origin).origin;
            const socketDef = defsById.get('integration.socket_realtime_url');
            const draftSocket = socketDef ? getValue('integration', socketDef).trim() : i.socket.url;
            const geocoderSource = {
              tenant: 'this tenant\'s Geocoder API Key',
              maps_fallback: 'the Maps API Key (fallback — add a Geocoder key)',
              platform: 'the platform\'s shared key from the server environment',
              none: 'no key — address search is unavailable',
            }[i.geocoder.source];
            const providerLabel = i.analytics.provider === 'google_analytics' ? 'Google Analytics 4' : i.analytics.provider === 'mixpanel' ? 'Mixpanel' : 'Off';
            return (
              <Alert severity={i.geocoder.source === 'none' || i.geocoder.source === 'maps_fallback' ? 'warning' : 'info'} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip size="small" color={i.geocoder.source === 'tenant' ? 'success' : 'warning'} label={`Address search uses: ${geocoderSource}`} />
                  <Chip size="small" variant="outlined" label={`Apps' realtime server: ${draftSocket || `${apiOrigin} (API server, default)`}`} />
                  <Chip size="small" variant="outlined" label={`Analytics: ${i.analytics.active ? `${providerLabel} · ${i.analytics.id}` : 'Off'}`} />
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <li><strong>Maps API Key</strong> is sent to this tenant&apos;s apps (app config) for map display, so it must be restricted in Google Cloud to your Android/iOS apps and Maps SDK for Android/iOS. <strong>Test key</strong> confirms it is valid and that it refuses server requests; a key that works from any server is flagged.</li>
                  <li><strong>Geocoder API Key</strong> stays on the server and is never sent to the apps. It needs Geocoding API (and Places API for nearby suggestions) only; the test checks those work and flags the key if it also opens unrelated APIs.</li>
                  <li>New keys are tested when you save; an invalid key, disabled billing or a missing required API blocks the save. Google cannot report a key&apos;s restriction settings directly, so the test probes them.</li>
                  <li><strong>Socket Realtime URL</strong> is optional: leave it empty and the apps connect to the API server. A custom server must answer a Socket.IO handshake over https/wss before it is saved.</li>
                  <li>Keys and URLs belong to this tenant only (keys are stored encrypted); other tenants&apos; apps never receive them. Changes reach the apps on their next app-config load and apply to the next address search.</li>
                </Box>
              </Alert>
            );
          })()}
          {activeCategory === 'general' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Timezone, currency and language are saved to this tenant&apos;s record, which the platform reads for new requests.
              Language is a code (e.g. <strong>en</strong>); its formatting locale (e.g. en-US) and the currency&apos;s symbol and decimals come from the Languages and Currencies masters.
            </Alert>
          )}
          <Grid container spacing={2.5}>
            {categoryDef?.keys.map((keyDef: SettingKeyDef) => {
              const id = `${activeCategory}.${keyDef.key}`;
              const stored = settingsMap.get(id);
              const value = getValue(activeCategory, keyDef);
              const error = draftErrors[id] || null;
              const dirty = draft[id] !== undefined;
              const gate = keyDef.activeWhen;
              const gateDef = gate ? defsById.get(`${activeCategory}.${gate.key}`) : undefined;
              const gateValue = gateDef ? getValue(activeCategory, gateDef) : undefined;
              const inactive = gate && gateDef && !isGateOpen(gate, gateValue)
                ? `Not in use while ${gateDef.label} is ${gate.equals === 'true' ? 'OFF' : gateDef.options?.find((o) => o.value === gateValue)?.label || gateValue}${stored?.hasValue ? ' — the saved value is kept for later' : ''}`
                : null;
              const testKey = activeCategory === 'integration' && (INTEGRATION_TEST_KEYS as string[]).includes(keyDef.key)
                ? keyDef.key as IntegrationTestKey
                : null;
              const verification = testKey ? integrationVerification(ctx, testKey) : null;
              const testResult = testKey ? integrationTests[testKey] : undefined;
              const issue = inactive ? null : (stored?.hasValue ? stored.issue : null) || stored?.warning || null;
              const unsetEmpty = !stored?.hasValue && !keyDef.defaultValue;
              const advisory = !issue && !inactive ? stored?.advisory || null : null;
              const savedAt = stored?.updatedAt ? new Date(stored.updatedAt).toLocaleString() : null;
              return (
                <Grid item xs={12} md={6} key={keyDef.key}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: '100%',
                      borderColor: error ? 'error.main' : dirty ? 'warning.main' : undefined,
                      bgcolor: inactive ? alpha(theme.palette.text.primary, 0.03) : issue ? alpha(theme.palette.error.main, 0.03) : stored?.hasValue ? alpha(theme.palette.success.main, 0.04) : 'background.paper',
                      opacity: inactive && !dirty ? 0.7 : 1,
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1}>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">{keyDef.key}</Typography>
                      <Stack direction="row" spacing={0.5}>
                        {dirty && <Chip label="Unsaved" size="small" color="warning" variant="outlined" />}
                        {inactive ? (
                          <Tooltip title={`${inactive}. It is not counted as configured and is not validated until it is in use.`}>
                            <Chip label="Not in use" size="small" variant="outlined" />
                          </Tooltip>
                        ) : issue ? (
                          <Tooltip title={issue}><Chip label="Needs attention" size="small" color="error" /></Tooltip>
                        ) : advisory ? (
                          <Tooltip title={`${advisory}${stored?.hasValue ? '' : ' (default value)'}`}>
                            <Chip
                              label={activeCategory === 'integration'
                                ? (!testKey ? 'Analytics off'
                                  : !stored?.hasValue ? (ctx?.integration?.geocoder.source === 'none' ? 'Unavailable' : 'Fallback in use')
                                    : verification?.current && verification.ok ? 'Needs restricting' : 'Not verified')
                                : activeCategory === 'ai' ? (keyDef.key === 'ocr_enabled' ? 'Will fail' : 'Not verified')
                                : activeCategory === 'partner_onboarding' ? (value === 'false' ? 'Weaker · Off' : 'Gap · On')
                                : activeCategory === 'locale' ? (keyDef.key === 'fx_provider' ? 'Rates not current' : advisory.startsWith('No effect') ? 'No effect · On' : 'Limited history')
                                : activeCategory !== 'security' ? 'No effect yet' : keyDef.type === 'boolean' ? `Not secure · ${value === 'true' ? 'On' : 'Off'}` : 'Below recommended'}
                              size="small"
                              color="warning"
                            />
                          </Tooltip>
                        ) : stored?.hasValue && verification?.current && verification.ok ? (
                          <Tooltip title={`Saved for this tenant and verified ${new Date(verification.at).toLocaleString()}${verification.restricted ? ' · key is restricted' : ''}`}>
                            <Chip label="Verified" size="small" color="success" />
                          </Tooltip>
                        ) : stored?.hasValue ? (
                          <Tooltip title={`${keyDef.type === 'boolean' ? 'A valid on/off value is saved for this tenant — "Configured" is not the same as enabled. ' : 'Saved for this tenant'}${savedAt ? ` · last changed ${savedAt}` : ''}`}>
                            <Chip
                              label={keyDef.type === 'boolean' ? `Configured · ${stored.value === 'true' ? 'On' : 'Off'}` : 'Configured'}
                              size="small"
                              color="success"
                              variant={keyDef.type === 'boolean' ? 'outlined' : 'filled'}
                            />
                          </Tooltip>
                        ) : unsetEmpty ? (
                          <Tooltip title="No value saved for this tenant and there is no default">
                            <Chip label="Not set" size="small" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Not saved for this tenant — the default shown is used">
                            <Chip label="Default" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                    <SettingFieldInput
                      def={keyDef}
                      category={activeCategory}
                      value={value}
                      error={error}
                      options={optionsFor(activeCategory, keyDef)}
                      hint={[rangeHint(keyDef, ctx), fieldPreview(keyDef, value, ctx)].filter(Boolean).join(' · ') || null}
                      currencySymbol={tenantCurrency(ctx)?.symbol}
                      currencyDecimals={tenantCurrency(ctx)?.decimalPlaces}
                      onChange={(v) => setValue(activeCategory, keyDef, v)}
                      fileInfo={dirty ? null : stored?.fileInfo}
                      storageMode={ctx?.storage?.provider}
                      onFileUploaded={() => {
                        queryClient.invalidateQueries({ queryKey: ['settings'] });
                        queryClient.invalidateQueries({ queryKey: ['settings-context'] });
                        setDraft((prev) => {
                          const next = { ...prev };
                          delete next[id];
                          return next;
                        });
                        void refreshTenantSession(dispatch);
                        setSnack('File uploaded and saved');
                      }}
                    />
                    {inactive && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        {inactive}
                      </Typography>
                    )}
                    {issue && !dirty && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 0.75 }}>
                        {issue}
                      </Typography>
                    )}
                    {advisory && !dirty && (
                      <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.75 }}>
                        {advisory}
                      </Typography>
                    )}
                    {testKey && (value.trim() || stored?.hasValue) && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!!error || integrationTestMutation.isPending}
                          startIcon={integrationTestMutation.isPending && integrationTestMutation.variables?.key === testKey ? <CircularProgress size={14} /> : undefined}
                          onClick={() => integrationTestMutation.mutate({ key: testKey, value: dirty ? value : undefined })}
                        >
                          {testKey === 'socket_realtime_url' ? 'Test connection' : 'Test key'}{dirty ? ' (unsaved value)' : ''}
                        </Button>
                        {testResult && (
                          <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                            {testResult.steps.map((st) => (
                              <li key={st.step}>
                                <Typography variant="caption" color={!st.ok ? 'error.main' : st.warning ? 'warning.main' : 'success.main'}>
                                  {!st.ok ? '✗' : st.warning ? '!' : '✓'} {st.message}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                    {keyDef.usedBy && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Used by: {keyDef.usedBy}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary">
            Category: <strong>{activeCategory}</strong> · Changes apply only to tenant <strong>{ctxData?.tenant?.name || '…'}</strong>
            {' '}and take effect for new requests once saved. Every save is recorded in the audit log.
          </Typography>
        </Paper>
        {dirtyCount > 0 && (
          <Paper
            elevation={6}
            sx={{
              position: 'sticky', bottom: 16, mt: 2, p: 1.5, px: 2, borderRadius: 3, zIndex: 5,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
              border: 1, borderColor: errorCount ? 'error.main' : 'warning.main',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
              {errorCount ? ` · fix ${errorCount} invalid value${errorCount === 1 ? '' : 's'} to save` : ''}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<Undo />} onClick={() => { setDraft({}); setServerErrors({}); }} disabled={saveMutation.isPending}>
                Discard
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Save />}
                disabled={errorCount > 0 || saveMutation.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                Save {dirtyCount} change{dirtyCount === 1 ? '' : 's'}
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => !saveMutation.isPending && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Apply {dirtyCount} change{dirtyCount === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            These take effect immediately for new requests on tenant <strong>{ctxData?.tenant?.name || 'this tenant'}</strong> and are recorded in the audit log.
          </Typography>
          <Stack spacing={1}>
            {pendingChanges.map((c) => (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{c.categoryLabel}</Typography>
                <Typography variant="body2" fontWeight={700}>{c.label}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {c.secret ? 'Secret value will be replaced' : (
                    <>
                      <Box component="span" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>{c.fromLabel || '(empty)'}</Box>
                      {' → '}
                      <strong>{c.toLabel || '(empty)'}</strong>
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saveMutation.isPending}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Apply changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={snack?.severity === 'error' ? 8000 : 6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack?.severity || 'success'} variant="filled" onClose={() => setSnack(null)} sx={{ maxWidth: 640 }}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
